/**
 * Les modules de l'application, et ce qui s'affiche dans le rond de société.
 *
 * **La liste vient du cadrage §6**, recopiée sans rien y ajouter :
 * « Accueil · Exploitation · Affrètement · Annuaire · Moyens · Finance ·
 * Paramètres ». Un seul mot change — **Moyens devient Ressources**, renommage
 * demandé par l'utilisateur le 03/09/2026.
 *
 * **`ouvert` dit ce qui existe, pas ce qui est prévu.** Un module fermé se voit
 * dans la liste sans être cliquable : la personne sait où va le produit sans
 * pouvoir ouvrir une porte qui ne mène nulle part. Le jour où un module ouvre,
 * une ligne change ici et rien d'autre.
 *
 * **`Paramètres` est un module comme les autres**, seulement détaché par un
 * séparateur — c'est ce que dit `part` : il est mis à part dans la liste. Il ne
 * figure PAS dans le menu du rond, où l'utilisateur l'avait vu en double le
 * 03/09/2026 : le rond porte la personne (mon compte, se déconnecter), la liste
 * porte l'application.
 */

/** Une entrée du sous-menu, sous la barre haute. */
export interface SousMenu {
  /** Ce qui identifie l'entrée dans l'écran — jamais montré. */
  readonly cle: string;
  readonly nom: string;
}

export interface Module {
  readonly cle: string;
  readonly nom: string;
  /** Cet écran existe-t-il ? Faux tant qu'il n'est pas construit. */
  readonly ouvert: boolean;
  /** Détaché du reste de la liste par un séparateur. */
  readonly part?: boolean;
}

/**
 * Les modules, dans l'ordre du cadrage §6.
 *
 * Ouverts au 03/09/2026 : l'annuaire et les ressources. **Exploitation ouvre
 * le 04/09/2026** avec les ordres de transport puis le planning, et
 * **Paramètres le même jour** avec les trois listes que chaque société règle
 * elle-même. Le reste attend son étape.
 */
export const MODULES: readonly Module[] = [
  { cle: 'accueil', nom: 'Accueil', ouvert: false },
  { cle: 'exploitation', nom: 'Exploitation', ouvert: true },
  { cle: 'affretement', nom: 'Affrètement', ouvert: false },
  { cle: 'annuaire', nom: 'Annuaire', ouvert: true },
  { cle: 'ressources', nom: 'Ressources', ouvert: true },
  // FINANCE OUVRE LE 07/09/2026, avec le premier lot du coût de revient : ce
  // que chaque société paramètre. Il ne calcule encore aucun coût de transport
  // — l'imputation et la lecture du mois sont les lots 2 et 3.
  { cle: 'finance', nom: 'Finance', ouvert: true },
  { cle: 'parametres', nom: 'Paramètres', ouvert: true, part: true },
];

/**
 * Les mots qui ne valent pas une initiale.
 *
 * Liste FERMÉE et courte : sans elle, « Le Transport du Nord » donnerait « LT »
 * là où personne n'écrirait autre chose que « TN ». Elle ne contient que des
 * mots de liaison — **aucune forme juridique**. Les mettre (SARL, SAS…)
 * demanderait d'en tenir la liste à jour pour tous les clients de Stafa, alors
 * qu'en français elles se placent presque toujours APRÈS le nom, où les deux
 * premiers mots ne vont pas les chercher.
 */
const LIAISONS = new Set([
  'de',
  'des',
  'du',
  'la',
  'le',
  'les',
  'et',
  'en',
  'a',
  'au',
  'aux',
  'l',
  'd',
]);

/**
 * Les initiales affichées dans le rond, à droite de la barre haute.
 *
 * « TNS Logistique » → « TL ». Un seul mot → ses deux premières lettres
 * (« Transalliance » → « TR »), parce qu'une seule lettre dans un rond de
 * 34 px se lit comme une puce, pas comme un nom.
 *
 * **Calculées, pas enregistrées**, et c'est à reprendre le jour où une société
 * voudra les siennes : il n'existe aujourd'hui aucune colonne pour les poser.
 * Rien ne se perd en attendant — la fonction ne fait que lire la raison
 * sociale.
 *
 * Rend une chaîne VIDE si rien ne s'y prête (nom vide, ponctuation seule) :
 * c'est à l'appelant de décider quoi montrer, et non à cette fonction
 * d'inventer un « ? ».
 */
export function initiales(raisonSociale: string): string {
  const mots = raisonSociale.split(/[^\p{L}\p{N}]+/u).filter((mot) => mot.length > 0);
  if (mots.length === 0) return '';

  // On écarte les mots de liaison, SAUF s'il ne reste rien : « Le Havre » vaut
  // mieux que rien du tout si la raison sociale n'est faite que de liaisons.
  const retenus = mots.filter((mot) => !LIAISONS.has(mot.toLowerCase()));
  const utiles = retenus.length > 0 ? retenus : mots;

  if (utiles.length === 1) {
    return (utiles[0] as string).slice(0, 2).toUpperCase();
  }
  return (
    (utiles[0] as string).slice(0, 1) + (utiles[1] as string).slice(0, 1)
  ).toUpperCase();
}
